import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, Chip, CircularProgress, Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { content } from '../api/client';

const CompletionPage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    content.modules()
      .then(({ data: modules }) => {
        const mod = modules.find(m => m.id === moduleId);
        setModule(mod);
      })
      .finally(() => setLoading(false));
  }, [moduleId]);

  const getQuizResult = (courseId) => {
    const key = `quiz_${moduleId}_${courseId}`;
    return JSON.parse(localStorage.getItem(key) || 'null');
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (!module) return <Typography>Module not found.</Typography>;

  const courses = module.courses || [];
  const allPassed = courses.every(c => {
    const result = getQuizResult(c.id);
    return result?.passed;
  });

  const totalLessons = courses.reduce((sum, c) => sum + (c.lessonCount || c.lessons?.length || 0), 0);
  const avgScore = courses.length > 0
    ? Math.round(
        courses.reduce((sum, c) => {
          const r = getQuizResult(c.id);
          return sum + (r?.score || 0);
        }, 0) / courses.length
      )
    : 0;

  const moduleColor = {
    code_intro: '#f44336',
    r_eco: '#9c27b0',
    r_bio: '#2196f3',
    r_cell: '#ff9800',
    py_worm: '#4caf50',
    resources: '#ffca28'
  }[moduleId] || '#2196f3';

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3, textAlign: 'center' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${moduleColor} 0%, ${moduleColor}cc 100%)`,
          borderRadius: 4,
          p: 5,
          mb: 4,
          color: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }}
      >
        <EmojiEventsIcon sx={{ fontSize: 80, mb: 2, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Module Complete!
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ opacity: 0.9 }}>
          {module.title}
        </Typography>
        {allPassed ? (
          <Chip
            label="ALL QUIZZES PASSED"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '1rem', px: 2, py: 3, mt: 1 }}
          />
        ) : (
          <Chip
            label="SOME QUIZZES PENDING"
            sx={{ bgcolor: 'rgba(255,165,0,0.3)', color: 'white', fontSize: '1rem', px: 2, py: 3, mt: 1 }}
          />
        )}
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h3" fontWeight="bold" color={moduleColor}>
              {courses.length}
            </Typography>
            <Typography variant="body1" color="text.secondary">Courses Completed</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h3" fontWeight="bold" color={moduleColor}>
              {totalLessons}
            </Typography>
            <Typography variant="body1" color="text.secondary">Lessons Covered</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h3" fontWeight="bold" color={moduleColor}>
              {avgScore}%
            </Typography>
            <Typography variant="body1" color="text.secondary">Average Quiz Score</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3, textAlign: 'left' }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Your Progress
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {courses.map((course, i) => {
          const result = getQuizResult(course.id);
          return (
            <Box
              key={course.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 1,
                borderRadius: 2,
                mb: 1,
                bgcolor: result?.passed ? 'success.50' : 'grey.50',
                border: '1px solid',
                borderColor: result?.passed ? 'success.200' : 'grey.200'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon
                  sx={{ color: result?.passed ? 'success.main' : 'grey.400' }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {course.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {course.lessonCount || course.lessons?.length || 0} lessons
                  </Typography>
                </Box>
              </Box>
              <Box textAlign="right">
                {result ? (
                  <>
                    <Chip
                      label={result.passed ? 'PASSED' : 'NOT YET'}
                      color={result.passed ? 'success' : 'default'}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      Quiz: {result.score}%
                    </Typography>
                  </>
                ) : (
                  <Chip label="QUIZ PENDING" color="warning" size="small" variant="outlined" />
                )}
              </Box>
            </Box>
          );
        })}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate(`/module/${moduleId}`)}
          sx={{ px: 4 }}
        >
          Back to Module
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/')}
          sx={{ px: 4, bgcolor: moduleColor, '&:hover': { bgcolor: moduleColor, filter: 'brightness(0.85)' } }}
        >
          Explore More Modules
        </Button>
      </Box>
    </Box>
  );
};

export default CompletionPage;
